import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Card,
  CardContent,
  Alert
} from '@mui/material';
import {
  Refresh,
  Security,
  AdminPanelSettings,
  People,
  CheckCircle,
  Error,
  Warning,
  Info
} from '@mui/icons-material';

const KratosDetailsModal = ({
  open,
  onClose,
  kratosStatus,
  onRefresh,
  onCheckAdmin,
  onCheckUsers,
  loading = false,
  adminLoading = false
}) => {
  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
      case 'success':
      case 'connected':
        return <CheckCircle color="success" />;
      case 'error':
      case 'unhealthy':
        return <Error color="error" />;
      case 'warning':
        return <Warning color="warning" />;
      default:
        return <Info color="info" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
      case 'success':
      case 'connected':
        return 'success';
      case 'error':
      case 'unhealthy':
        return 'error';
      case 'warning':
        return 'warning';
      default:
        return 'info';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Security color="primary" />
        Детальная информация Kratos
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {/* Public API Status */}
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Security />
                  Public API
                </Typography>
                <Chip
                  icon={getStatusIcon(kratosStatus.public?.status)}
                  label={kratosStatus.public?.status || 'unknown'}
                  color={getStatusColor(kratosStatus.public?.status)}
                  variant="outlined"
                />
              </Box>
              
              {kratosStatus.public ? (
                <Stack spacing={1}>
                  <Typography variant="body2">
                    <strong>Сообщение:</strong> {kratosStatus.public.message || 'Нет информации'}
                  </Typography>
                  {kratosStatus.public.responseTime && (
                    <Typography variant="body2">
                      <strong>Время ответа:</strong> {kratosStatus.public.responseTime}ms
                    </Typography>
                  )}
                  {kratosStatus.public.timestamp && (
                    <Typography variant="body2">
                      <strong>Проверено:</strong> {new Date(kratosStatus.public.timestamp).toLocaleString()}
                    </Typography>
                  )}
                  {kratosStatus.public.error && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {kratosStatus.public.error}
                    </Alert>
                  )}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Информация не доступна
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Admin API Status */}
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AdminPanelSettings />
                  Admin API
                </Typography>
                <Chip
                  icon={getStatusIcon(kratosStatus.admin?.status)}
                  label={kratosStatus.admin?.status || 'unknown'}
                  color={getStatusColor(kratosStatus.admin?.status)}
                  variant="outlined"
                />
              </Box>
              
              {kratosStatus.admin ? (
                <Stack spacing={1}>
                  <Typography variant="body2">
                    <strong>Сообщение:</strong> {kratosStatus.admin.message || 'Нет информации'}
                  </Typography>
                  {kratosStatus.admin.responseTime && (
                    <Typography variant="body2">
                      <strong>Время ответа:</strong> {kratosStatus.admin.responseTime}ms
                    </Typography>
                  )}
                  {kratosStatus.admin.timestamp && (
                    <Typography variant="body2">
                      <strong>Проверено:</strong> {new Date(kratosStatus.admin.timestamp).toLocaleString()}
                    </Typography>
                  )}
                  {kratosStatus.admin.error && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {kratosStatus.admin.error}
                    </Alert>
                  )}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Информация не доступна
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Users Statistics */}
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <People />
                  Статистика пользователей
                </Typography>
                <Chip
                  icon={getStatusIcon(kratosStatus.users?.status)}
                  label={kratosStatus.users?.status || 'unknown'}
                  color={getStatusColor(kratosStatus.users?.status)}
                  variant="outlined"
                />
              </Box>
              
              {kratosStatus.users ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h4" color="primary.main">
                        {kratosStatus.users.total_users || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Всего пользователей
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h4" color="success.main">
                        {kratosStatus.users.active_users || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Активных
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h4" color="error.main">
                        {kratosStatus.users.inactive_users || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Неактивных
                      </Typography>
                    </Card>
                  </Grid>
                  
                  {kratosStatus.users.sample_users && kratosStatus.users.sample_users.length > 0 && (
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle2" gutterBottom>
                        Примеры пользователей:
                      </Typography>
                      <Stack spacing={1}>
                        {kratosStatus.users.sample_users.slice(0, 3).map((user, index) => (
                          <Box key={index} sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="body2">
                              <strong>ID:</strong> {user.id?.substring(0, 8)}... |{' '}
                              <strong>Email:</strong> {user.traits?.email} |{' '}
                              <strong>Статус:</strong> {user.state}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Grid>
                  )}
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Статистика пользователей не доступна
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Detailed Information */}
          {kratosStatus.detailed && (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Info />
                  Детальная информация
                </Typography>
                
                <Stack spacing={2}>
                  <Typography variant="body2">
                    <strong>Общий статус:</strong> {kratosStatus.detailed.status}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Сообщение:</strong> {kratosStatus.detailed.message}
                  </Typography>
                  {kratosStatus.detailed.timestamp && (
                    <Typography variant="body2">
                      <strong>Время проверки:</strong> {new Date(kratosStatus.detailed.timestamp).toLocaleString()}
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* Error Display */}
          {(kratosStatus.public?.error || kratosStatus.admin?.error || kratosStatus.users?.error) && (
            <Alert severity="error">
              <Typography variant="subtitle2" gutterBottom>
                Обнаружены ошибки:
              </Typography>
              <Stack spacing={0.5}>
                {kratosStatus.public?.error && (
                  <Typography variant="body2">• Public API: {kratosStatus.public.error}</Typography>
                )}
                {kratosStatus.admin?.error && (
                  <Typography variant="body2">• Admin API: {kratosStatus.admin.error}</Typography>
                )}
                {kratosStatus.users?.error && (
                  <Typography variant="body2">• Users: {kratosStatus.users.error}</Typography>
                )}
              </Stack>
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button onClick={onClose} color="inherit">
          Закрыть
        </Button>
        
        <Button
          onClick={onCheckUsers}
          startIcon={loading ? <CircularProgress size={16} /> : <People />}
          disabled={loading}
          variant="outlined"
        >
          Обновить статистику
        </Button>
        
        <Button
          onClick={onCheckAdmin}
          startIcon={adminLoading ? <CircularProgress size={16} /> : <AdminPanelSettings />}
          disabled={adminLoading}
          variant="outlined"
        >
          Проверить Admin API
        </Button>
        
        <Button
          onClick={onRefresh}
          startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
          disabled={loading}
          variant="contained"
        >
          Полная проверка
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default KratosDetailsModal;